import { Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Supplier } from '../../../warehouse/supplier/supplier';
import { SupplierService } from '../../../warehouse/supplier/supplier.service';
import {
    BestellungExtern,
    BestellungExternDokument,
    BestellungExternPosition,
    istFremdlaborBestellung,
} from '../bestellung-extern';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BestellungExternService } from '../bestellung-extern.service';
import { FileUploadService } from '../../../dialogs/file-upload-dialog/file-upload.service';
import { ConfirmDeleteDialogComponent } from '../../../dialogs/confirm-delete-dialog/confirm-delete-dialog.component';
import { UntypedFormControl, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import {
    isEinkaufLaborArticle,
    laborBestellEinheit,
    laborEinheitForm,
    laborInhaltAnzeige,
    laborMengePasstZumPack,
    laborPackFehler,
    laborPackInfo,
    Material,
} from '../../../warehouse/material/material';
import { MaterialService } from '../../../warehouse/material/material.service';
import { LocalStorageService } from '../../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { HttpParams } from '@angular/common/http';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { KieneSession } from '../../../../../projects/kiene-core/src/lib/services/local-storage/kiene-session';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../../projects/kiene-core/src/lib/utils/KieneUtils';

export interface BestellungExternDetailsDialogData {
    bestellungExternId: number;
}

@Component({
    selector: 'kiene-bestellung-extern-details',
    templateUrl: './bestellung-extern-details.component.html',
    styleUrls: ['./bestellung-extern-details.component.scss'],
})
export class BestellungExternDetailsComponent implements OnInit {
    isLoading = false;

    editAllowed = true;

    bestellungExternId: string;
    embeddedInDialog = false;
    kieneSession: KieneSession;
    supplier: Supplier = new Supplier();

    materials: Observable<Material[]>;

    materialCtrl: UntypedFormControl = new UntypedFormControl('', [Validators.required]);

    deliveryNotes: BestellungExternDokument[] = [];
    invoices: BestellungExternDokument[] = [];

    bestellungExtern: BestellungExtern = new BestellungExtern();

    canUserReleaseBestellungExtern = false;

    @ViewChild('materialInput')
    materialInput: ElementRef;

    @ViewChild('amountInput')
    amountInput: ElementRef;

    constructor(
        private readonly pdfViewerService: PdfViewerDesktopService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        private readonly fileUploadService: FileUploadService,
        private readonly service: BestellungExternService,
        private readonly router: Router,
        private readonly localStorageService: LocalStorageService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly materialService: MaterialService,
        private readonly supplierService: SupplierService,
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: BestellungExternDetailsDialogData | null,
        @Optional() private readonly hostDialogRef: MatDialogRef<BestellungExternDetailsComponent> | null,
    ) {
        if (this.dialogData?.bestellungExternId) {
            this.bestellungExternId = String(this.dialogData.bestellungExternId);
            this.embeddedInDialog = true;
        } else {
            this.bestellungExternId = this.activatedRoute.snapshot.paramMap.get('id');
        }
    }

    ngOnInit() {
        this.autocompleteMaterial();
        this.kieneSession = this.localStorageService.getCurrentUser();

        if (this.bestellungExternId) {
            if (this.bestellungExternId !== 'neu') {
                const id: number = Number.parseInt(this.bestellungExternId, 10);
                this.loadBestellungExtern(id);
            } else {
                this.editAllowed = true;
            }
        }
    }

    isUserAllowedToReleaseBestellungExtern(): void {
        if (
            (KieneUtils.includesId(this.kieneSession.benutzerrecht_ids, 1766) ||
                KieneUtils.includesId(this.kieneSession.benutzerrecht_ids, 1082)) &&
            KieneUtils.sameId(this.bestellungExtern.status_id, 22)
        ) {
            this.canUserReleaseBestellungExtern = true;
        } else {
            this.canUserReleaseBestellungExtern = false;
        }
    }

    hasBestellungStatus(...statusIds: number[]): boolean {
        return statusIds.some(id => KieneUtils.sameId(this.bestellungExtern?.status_id, id));
    }

    /** Labor: Anzahl der Lagereinheit. Inhalt nur, wenn eine Packung mehr als 1 enthält. */
    bestellmengeText(p: BestellungExternPosition, menge: number): string {
        const labor = Number(p?.laborartikel) > 0 || isEinkaufLaborArticle(p);
        const anzahl = Number(menge) || 0;
        if (!labor) {
            return anzahl + ' Stk.';
        }
        const inhaltQuelle = p?.einheit_roh || p?.einheit;
        const pack = laborEinheitForm(this.bestellEinheitRoh(p), anzahl);
        const inhalt = laborInhaltAnzeige(inhaltQuelle, p?.menge);
        return inhalt ? anzahl + ' ' + pack + ' à ' + inhalt : anzahl + ' ' + pack;
    }

    bestellEinheit(p: BestellungExternPosition): string {
        const labor = Number(p?.laborartikel) > 0 || isEinkaufLaborArticle(p);
        if (!labor) {
            return 'Stk.';
        }
        return laborEinheitForm(this.bestellEinheitRoh(p), p?.menge_soll);
    }

    private bestellEinheitRoh(p: BestellungExternPosition): string {
        return laborBestellEinheit(p?.einheit_alternativ, p?.einheit_roh || p?.einheit, p?.menge);
    }

    positionsName(p: BestellungExternPosition): string {
        const labor = Number(p?.laborartikel) > 0 || isEinkaufLaborArticle(p);
        if (!labor) {
            return p?.artikel_me || p?.artikel;
        }
        const name = String(p?.artikel || '').trim();
        const inhalt = laborInhaltAnzeige(p?.einheit_roh || p?.einheit, p?.menge);
        return inhalt ? name + ' (' + inhalt + ')' : p?.artikel_me || name;
    }

    packInfo(p: BestellungExternPosition): string {
        const labor = Number(p?.laborartikel) > 0 || isEinkaufLaborArticle(p);
        return labor ? laborPackInfo(p?.gebindegroesse) : '';
    }

    packFehler(p: BestellungExternPosition): string {
        const labor = Number(p?.laborartikel) > 0 || isEinkaufLaborArticle(p);
        return labor ? laborPackFehler(p?.menge_soll, p?.gebindegroesse) : '';
    }

    private ungueltigePackmenge(): string {
        for (const p of this.bestellungExtern?.positionen || []) {
            const fehler = this.packFehler(p);
            if (fehler) {
                const name = p.artikel || p.artikel_me || 'Artikel';
                return name + ': ' + fehler;
            }
        }
        return '';
    }

    mindestbestellHinweis(p: BestellungExternPosition): string {
        const labor = Number(p?.laborartikel) > 0 || isEinkaufLaborArticle(p);
        if (!labor) {
            return '';
        }
        const min = Number(p?.bestellmenge);
        const anzahl = Number(p?.menge_soll);
        if (!(min > 0) || !(anzahl > 0) || anzahl >= min) {
            return '';
        }
        const pack = laborEinheitForm(
            laborBestellEinheit(p?.einheit_alternativ, p?.einheit_roh || p?.einheit, p?.menge),
            min,
        );
        return `Mindestbestellmenge ${min} ${pack} ist noch nicht erreicht.`;
    }

    zeigtUntersuchungsauftrag(): boolean {
        return istFremdlaborBestellung(this.bestellungExtern);
    }

    goBack() {
        if (this.embeddedInDialog && this.hostDialogRef) {
            this.hostDialogRef.close();
            return;
        }
        this.router.navigate(['/wawi/bestellung-extern/']);
    }

    loadBestellungExtern(id: number) {
        this.isLoading = true;
        const params = new HttpParams().set('bestellung_extern_id', id);
        this.api.get(this.apiBaseUrl + 'bestellung_extern/read_one.php', params).subscribe({
            next: response => {
                this.bestellungExtern = response;

                if (Number(this.bestellungExtern.status_id) <= 22) {
                    this.editAllowed = true;
                } else {
                    this.editAllowed = false;
                }
                this.isLoading = false;
                this.loadSupplier(this.bestellungExtern.lieferant_id);
                this.isUserAllowedToReleaseBestellungExtern();
                this.loadDeliveryNotes();
                this.loadInvoices();
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }

    loadSupplier(id: number) {
        this.isLoading = true;
        this.supplierService.getSupplierById(id).subscribe(
            response => {
                this.supplier = response;
                this.isLoading = false;
            },
            () => {
                this.isLoading = false;
            },
        );
    }

    autocompleteMaterial() {
        this.materials = this.materialCtrl.valueChanges.pipe(
            debounceTime(100),
            startWith(''),
            switchMap(value => {
                return this.searchMaterial(value);
            }),
        );
    }

    searchMaterial(value: string) {
        if (this.supplier !== undefined) {
            return this.materialService.searchSupplierMaterial(value, this.supplier).pipe(
                debounceTime(300),
                distinctUntilChanged(),
                map(response => response.records),
            );
        } else {
            return this.materialService.searchSupplierMaterial(value).pipe(
                debounceTime(300),
                distinctUntilChanged(),
                map(response => response.records),
            );
        }
    }

    removePosition(p: BestellungExternPosition) {
        const index = this.bestellungExtern.positionen.indexOf(p);
        this.bestellungExtern.positionen.splice(index, 1);
        if (this.bestellungExternId && this.bestellungExternId !== 'neu') {
            this.saveBestellungExtern(true, true);
        }
    }

    loadDeliveryNotes() {
        if (
            !this.localStorageService.currentUserHasPermission(1741) &&
            !this.localStorageService.currentUserHasPermission(1082)
        ) {
            return;
        }
        this.service.getDeliveryNotes(this.bestellungExtern).subscribe(response => {
            this.deliveryNotes = response.records;
        });
    }

    loadInvoices() {
        if (
            !this.localStorageService.currentUserHasPermission(1751) &&
            !this.localStorageService.currentUserHasPermission(1082)
        ) {
            return;
        }
        this.service.getInvoices(this.bestellungExtern).subscribe(response => {
            this.invoices = response.records;
        });
    }

    saveBestellungExtern(ohnePackpruefung = false, bleiben = false) {
        if (this.bestellungExtern != null) {
            const packFehler = ohnePackpruefung ? '' : this.ungueltigePackmenge();
            if (packFehler) {
                this.messageService.errorMessage(packFehler, null);
                return;
            }
            this.bestellungExtern.status_id_neu ??= 21;

            this.bestellungExtern.lieferant_id = this.supplier.lieferant_id;

            this.service.updateBestellungExtern(this.bestellungExtern).subscribe(
                response => {
                    this.bestellungExtern = response;
                    this.messageService.infoMessage(
                        'Die Bestellung ' +
                            this.bestellungExtern.bestellung_extern_id +
                            ' wurde erfolgreich gespeichert!',
                    );
                    if (!bleiben) {
                        this.router.navigate(['/wawi/bestellung-extern', this.bestellungExtern.bestellung_extern_id]);
                    }
                    this.bestellungExternId = this.bestellungExtern.bestellung_extern_id.toString();
                    this.loadBestellungExtern(this.bestellungExtern.bestellung_extern_id);
                },
                error => {
                    this.messageService.errorMessage('Fehler! Die Bestellung konnte nicht gespeichert werden! ', error);
                    this.loadBestellungExtern(Number.parseInt(this.bestellungExternId, 10));
                },
            );
        }
    }

    release() {
        const packFehler = this.ungueltigePackmenge();
        if (packFehler) {
            this.messageService.errorMessage(packFehler, null);
            return;
        }
        this.bestellungExtern.status_id_neu = 22;

        this.service.updateBestellungExtern(this.bestellungExtern).subscribe(
            response => {
                this.bestellungExtern = response;
                this.isUserAllowedToReleaseBestellungExtern();
                this.messageService.infoMessage(
                    'Die Bestellung ' + this.bestellungExtern.bestellung_extern_id + ' wurde freigegeben!',
                );
            },
            error => {
                this.messageService.errorMessage('Fehler! Die Bestellung konnte nicht freigegeben werden! ', error);
                this.loadBestellungExtern(Number.parseInt(this.bestellungExternId, 10));
            },
        );
    }

    downloadAlleDokumente(): void {
        if (!this.bestellungExternId || this.bestellungExternId === 'neu') {
            return;
        }
        this.pdfViewerService.openPdfViewer(
            new PdfViewerDialogInput(
                'bestellung_extern/pdf_merged.php',
                new HttpParams().set('bestellung_extern_id', this.bestellungExternId),
                'Bestellung ' + (this.bestellungExtern?.bestell_nr || this.bestellungExternId),
            ),
        );
    }

    order() {
        if (this.bestellungExtern != null) {
            const packFehler = this.ungueltigePackmenge();
            if (packFehler) {
                this.messageService.errorMessage(packFehler, null);
                return;
            }
            this.bestellungExtern.status_id_neu = 23;

            this.service.updateBestellungExtern(this.bestellungExtern).subscribe(
                response => {
                    this.bestellungExtern = response;
                    this.editAllowed = false;
                    this.messageService.infoMessage(
                        'Die Bestellung ' +
                            this.bestellungExtern.bestellung_extern_id +
                            ' wurde erfolgreich aufgegeben!',
                    );
                },
                error => {
                    this.messageService.errorMessage('Fehler! Die Bestellung konnte nicht aufgegeben werden! ', error);
                    this.loadBestellungExtern(Number.parseInt(this.bestellungExternId, 10));
                },
            );
        }
    }

    markAsBooked() {
        if (this.bestellungExtern != null) {
            this.bestellungExtern.status_id_neu = 29;

            this.service.updateBestellungExtern(this.bestellungExtern).subscribe({
                next: response => {
                    this.bestellungExtern = response;
                    this.editAllowed = false;
                    this.messageService.infoMessage(
                        'Die Bestellung ' +
                            this.bestellungExtern.bestellung_extern_id +
                            ' wurde erfolgreich als bestellt markiert!',
                    );
                },
                error: error => {
                    this.messageService.errorMessage(
                        'Fehler! Die Bestellung konnte nicht als bestellt markiert werden! ',
                        error,
                    );
                    this.loadBestellungExtern(Number.parseInt(this.bestellungExternId, 10));
                },
            });
        }
    }

    close() {
        if (this.bestellungExtern != null) {
            this.bestellungExtern.status_id_neu = 30; // geliefert manuell

            this.service.updateBestellungExtern(this.bestellungExtern).subscribe(
                response => {
                    this.bestellungExtern = response;
                    this.editAllowed = false;
                    this.messageService.infoMessage(
                        'Die Bestellung ' +
                            this.bestellungExtern.bestellung_extern_id +
                            ' wurde erfolgreich geschlossen!',
                    );
                },
                error => {
                    this.messageService.errorMessage('Fehler! Die Bestellung konnte nicht geschlossen werden! ', error);
                    this.loadBestellungExtern(Number.parseInt(this.bestellungExternId, 10));
                },
            );
        }
    }

    openAddDeliveryNoteDialog() {
        const ids: number[] = [];
        ids.push(this.bestellungExtern.bestellung_extern_id);
        this.fileUploadService.openAddFilesDialogIds(
            ids,
            'bestellung_extern_ids',
            'bestellung_extern_dokument/create_lieferschein.php',
            () => {
                this.service.getDeliveryNotes(this.bestellungExtern).subscribe(response => {
                    this.deliveryNotes = response.records;
                });
                this.loadBestellungExtern(this.bestellungExtern.bestellung_extern_id);
            },
            () => {},
        );
    }

    openAddInvoiceDialog() {
        const ids: number[] = [];
        ids.push(this.bestellungExtern.bestellung_extern_id);
        this.fileUploadService.openAddFilesDialogIds(
            ids,
            'bestellung_extern_ids',
            'bestellung_extern_dokument/create_rechnung.php',
            () => {
                this.service.getInvoices(this.bestellungExtern).subscribe(response => {
                    this.invoices = response.records;
                });
                this.loadBestellungExtern(this.bestellungExtern.bestellung_extern_id);
            },
            () => {},
        );
    }

    showDeliveryNotePdf(bed: BestellungExternDokument) {
        this.pdfViewerService.openPdfViewer(
            new PdfViewerDialogInput(
                'bestellung_extern_dokument/read_pdf_lieferschein.php',
                new HttpParams().set('bed_id', bed?.bed_id?.toString()),
                bed.dateiname,
            ),
        );
    }

    showInvoicePdf(bed: BestellungExternDokument) {
        import('../invoice-deliver-note-dialog/invoice-deliver-note-dialog.component').then(m => {
            const dialogRef = this.dialog.open(m.InvoiceDeliverNoteDialogComponent, {
                width: '100vw',
                maxWidth: '100vw',
                height: '100vh',
                panelClass: ['gemeinkosten-scan-review-dialog-panel', 'mat-dialog-no-padding'],
                data: {
                    files: [],
                    bestellung: this.bestellungExtern,
                    preferredKind: 'invoice',
                    dokument: bed,
                },
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result?.saved && this.bestellungExtern?.bestellung_extern_id) {
                    this.loadBestellungExtern(this.bestellungExtern.bestellung_extern_id);
                }
            });
        });
    }

    openRemoveDokument(bed: BestellungExternDokument) {
        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            width: '450px',
            data: { title: 'Löschen', text: 'Wollen Sie ' + bed.dateiname + ' wirklich löschen?' },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.service.deleteBestellungExternDokument(bed).subscribe(
                    () => {
                        this.messageService.infoMessage('Datei ' + bed.dateiname + ' wurde erfolgreich entfernt!');
                        this.loadBestellungExtern(Number.parseInt(this.bestellungExternId, 10));
                    },
                    error => {
                        this.messageService.errorMessage(
                            'Die Datei ' + bed.dateiname + ' konnte nicht entfernt werden! Fehler: ',
                            error,
                        );
                    },
                );
            }
        });
    }

    invoiceOption(option: string, iv: BestellungExternDokument) {
        if (option === 'loeschen') {
            this.openRemoveDokument(iv);
        }
    }
}
